import { useState, useEffect } from 'react';

// Sign-in welcome greeting.
//
// Two pieces:
//  1. A one-shot "just signed in" flag (sessionStorage) set by auth.login()
//     and consumed by <WelcomeGreeting> on the first screen after login, so
//     the greeting fires on a fresh sign-in but NOT on a plain refresh /
//     seamless auto-update reload (neither calls login()).
//  2. A per-role, per-device on/off preference (localStorage), toggled from
//     the Background menu — same model as the view-mode / layout pickers.

export const WELCOME_EVENT = '3c-welcome-changed';
const JUST_LOGGED_IN_KEY = '3c_just_logged_in';

export function markJustLoggedIn() {
  try { sessionStorage.setItem(JUST_LOGGED_IN_KEY, '1'); } catch (_) {}
}

// Read-and-clear: returns true at most once per sign-in.
export function consumeJustLoggedIn() {
  try {
    const v = sessionStorage.getItem(JUST_LOGGED_IN_KEY) === '1';
    if (v) sessionStorage.removeItem(JUST_LOGGED_IN_KEY);
    return v;
  } catch (_) { return false; }
}

function keyFor(role) { return '3c_welcome_' + (role || 'principal'); }

// Enabled by default — the greeting is the feature the user asked for.
// Only an explicit 'off' key disables it.
export function getWelcomeEnabled(role) {
  try { return localStorage.getItem(keyFor(role)) !== 'off'; }
  catch (_) { return true; }
}

export function setWelcomeEnabled(role, on) {
  try {
    if (on) localStorage.removeItem(keyFor(role));       // on = default = no key
    else    localStorage.setItem(keyFor(role), 'off');
  } catch (_) {}
  try { window.dispatchEvent(new Event(WELCOME_EVENT)); } catch (_) {}
}

export function useWelcomeEnabled(role) {
  const [on, setOn] = useState(() => getWelcomeEnabled(role));
  useEffect(() => {
    const sync = () => setOn(getWelcomeEnabled(role));
    sync();
    window.addEventListener(WELCOME_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(WELCOME_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, [role]);
  return on;
}

// Role-appropriate greeting copy (Sharp & professional tone), with a
// time-of-day salutation. Keyed by the same role strings used in auth.
export function greetingFor(role) {
  const h = new Date().getHours();
  const part = h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
  const MAP = {
    owner:         { emoji: '👑', eyebrow: 'CEO',      line: 'Your shop, at a glance.' },
    principal:     { emoji: '⚙️', eyebrow: 'ENGINEER', line: 'All systems online.' },
    detailer:      { emoji: '✨', eyebrow: 'DETAILER', line: 'Ready when you are.' },
    'automotive-supervisor': { emoji: '🔧', eyebrow: 'SUPERVISOR', line: 'Ready when you are.' },
    'tax-advisor': { emoji: '📊', eyebrow: 'ADVISOR',  line: 'The books are ready.' },
    otto:          { emoji: '🚗', eyebrow: 'DEALER',   line: 'Book, quote, track.' },
  };
  const m = MAP[role] || { emoji: '👋', eyebrow: 'WELCOME', line: 'Good to see you.' };
  return { part, ...m };
}
